import { m } from "@plannello/i18n/messages";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { InlineEdit } from "@/components/ui";
import { useProductItems } from "@/data/items";
import { KEY_RE, patchTarget, type RenameTarget } from "@/data/spaces";
import { pushUndo } from "@/data/undo";
import { ApiError } from "@/lib/api";
import { toast } from "@/lib/toast";

/**
 * Inline fields for the name, description and key of a product or space (shown to product and space admins; everyone
 * else sees plain text). Names and descriptions save on Enter or blur; a key change is applied with Enter or blur and
 * offers Undo (which renames back, and old keys keep working both ways).
 */

const saveFailed = (e: unknown) => (e instanceof ApiError && e.isNetwork ? m.error_network() : m.rename_save_failed());

export function NameField({
  target,
  value,
  label,
  className,
}: {
  target: RenameTarget;
  value: string;
  label: string;
  className?: string;
}) {
  return (
    <InlineEdit
      value={value}
      label={label}
      className={className}
      spellCheck
      maxLength={200}
      onSave={async (name) => void (await patchTarget(target, { name }))}
      errorMessage={saveFailed}
    />
  );
}

export function DescriptionField({
  target,
  value,
  label,
  className,
}: {
  target: RenameTarget;
  value: string;
  label: string;
  className?: string;
}) {
  return (
    <InlineEdit
      multiline
      allowEmpty
      value={value}
      label={label}
      className={className}
      placeholder={m.rename_description_add()}
      spellCheck
      maxLength={10_000}
      onSave={async (description) => void (await patchTarget(target, { description: description || null }))}
      errorMessage={saveFailed}
    />
  );
}

/** Where a key lives in the URL: /p/KEY/... for a product, /s/KEY (and /p/KEY for its space-wide product) for a space. */
function rewriteHref(kind: RenameTarget["kind"], from: string, to: string): string | null {
  const here = window.location;
  const segment = kind === "space" ? "(?:p|s)" : "p";
  const path = here.pathname.replace(new RegExp(`^/(${segment})/${from}(?=/|$)`, "i"), (_, s: string) => `/${s}/${to}`);
  const search = here.search.replace(new RegExp(`([?&]peek=)${from}-`, "gi"), `$1${to}-`);
  const href = `${path}${search}${here.hash}`;
  return href === `${here.pathname}${here.search}${here.hash}` ? null : href;
}

/**
 * A key change moves the page to the new URL, which mounts the page afresh (routes are keyed by their params), so the
 * field the user was typing in is gone. The new key field takes the focus back, so the keyboard user stays where they were.
 */
let refocusKeyField = false;

/** The key chip of a product or space header, edited in place. */
export function KeyField({ target, productId }: { target: RenameTarget; productId: string }) {
  const router = useRouter();
  const [restoreFocus] = useState(() => {
    const v = refocusKeyField;
    refocusKeyField = false;
    return v;
  });
  // The preview uses a real item number of the product, so "PLZ-12 becomes SHOP-12" reads like the user's own keys.
  const items = useProductItems([productId]).items;
  const example = items.reduce((max, i) => Math.max(max, Number(i.key.slice(i.key.lastIndexOf("-") + 1)) || 0), 0) || 1;
  const label = target.kind === "space" ? m.rename_space_key() : m.rename_product_key();

  const rename = async (kind: RenameTarget["kind"], from: string, to: string, focusAfter = false) => {
    await patchTarget({ kind, key: from }, { key: to });
    refocusKeyField = focusAfter;
    const href = rewriteHref(kind, from, to);
    if (href) router.history.replace(href);
  };

  return (
    <InlineEdit
      value={target.key}
      label={label}
      className="font-mono text-xs leading-5 text-fg-subtle"
      spellCheck={false}
      autoFocus={restoreFocus}
      normalize={(raw) =>
        raw
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, "")
          .slice(0, 10)
      }
      validate={(draft) => (KEY_RE.test(draft) ? null : m.rename_key_invalid())}
      preview={(draft) => m.rename_key_preview({ from: `${target.key}-${example}`, to: `${draft}-${example}` })}
      errorMessage={(e) =>
        e instanceof ApiError && e.reason === "key_taken"
          ? m.rename_key_taken({ key: String(e.details?.key ?? "") })
          : e instanceof ApiError && e.reason === "invalid_key"
            ? m.rename_key_invalid()
            : e instanceof ApiError && e.isNetwork
              ? m.error_network()
              : m.rename_key_failed()
      }
      onSave={async (to) => {
        const from = target.key;
        await rename(target.kind, from, to, true);
        const undo = pushUndo(() => {
          void rename(target.kind, to, from).catch(() => toast.show({ title: m.rename_key_failed(), tone: "danger" }));
        });
        toast.undo(m.rename_key_changed_toast({ key: to }), undo, m.common_undo());
      }}
    />
  );
}
